import useSWRInfinite from "swr/infinite";
import { NOOP, endpointParamsExact, flattenedQueryParams, } from "./query-parser";
import { buildUrl } from "./build-url";
import { fetcher } from "./fetcher";
/**
 * A hook that provides an interface for making API requests.
 *
 * @param {UseInfiniteApiWithOptions<Q>} options - An object containing the following properties:
 *   - endpoint: The API endpoint to make the request to.
 *   - endpointParams: An object containing any parameters to be included in the endpoint URL.
 *   - queryParams: An object containing any query parameters to be included in the request URL.
 *   - shouldMakeRequest: A boolean indicating whether the request should be made.
 *   - swrOptions: An object containing options for the SWR hook.
 * @return {useInfiniteAPIResponseInterface<T>} An object containing the SWR infinite hook response
 */
export const useInfiniteAPI = ({ endpoint, endpointParams = {}, queryParams, shouldMakeRequest = true, swrOptions = {}, }) => {
    const queryParamsValue = Object.values(queryParams || NOOP);
    const endpointParamsValue = endpointParams
        ? Object.values(endpointParams)
        : [];
    const hasAllParams = ![...queryParamsValue, ...endpointParamsValue].includes(null);
    const getKey = (pageIndex, previousPageData) => {
        var _a, _b;
        if (!shouldMakeRequest || !hasAllParams) {
            // do not make the request by returning null
            return null;
        }
        const key = Object.keys(previousPageData || {}).find((k) => k !== "meta");
        if (previousPageData && key && !((_a = previousPageData === null || previousPageData === void 0 ? void 0 : previousPageData[key]) === null || _a === void 0 ? void 0 : _a.length))
            // reached the end
            return null;
        // first page, we don't have `previousPageData`
        if (pageIndex === 0) {
            return buildUrl({
                endpoint,
                endpointParams: endpointParamsExact(endpointParams),
                queryParams: flattenedQueryParams(queryParams),
            });
        }
        // add the cursor to the API endpoint
        return buildUrl({
            endpoint,
            endpointParams: endpointParamsExact(endpointParams),
            queryParams: flattenedQueryParams({
                ...queryParams,
                after: (_b = previousPageData.meta) === null || _b === void 0 ? void 0 : _b.cursors.after,
            }),
        });
    };
    return useSWRInfinite(getKey, fetcher, swrOptions);
};
